import parse from 'html-react-parser';
import React from "react";
import { useNavigate } from "react-router-dom";
import { Edit2, Eye, Clock } from "lucide-react";
import { capitalize } from "../../../../components/tools/utils.jsx";
import { useAuthContext } from "../../../../components/providers/AuthProvider.jsx";
import ActionsDropdown from "./ActionsDropdown.jsx";
import { editorPathForModel } from '../../../../products/registry';
import { dateCaption, formatAbsolute } from './dates.js';
import ProductTypeBadge from '../../../../products/ProductTypeBadge.jsx';
import ARViewer from '../../../Customiz3r/AR/ARViewer.jsx';

function removeWidthAndHeight(svgString) {
    return svgString
        .replace(/width="[^"]*"/, '')
        .replace(/height="[^"]*"/, '');
}

function Modelo3dCardList({ modelo, onDelete, onDuplicated, sortBy }) {
    const navigate = useNavigate();
    const authContext = useAuthContext();

    return (
        <div className="group flex items-stretch bg-base-100 rounded-xl
                        shadow-md border border-base-200
                        hover:shadow-lg hover:-translate-y-0.5 hover:border-primary/30
                        transition-all duration-200 overflow-visible h-28">

            {/* Accent stripe */}
            <div className="w-1 shrink-0 bg-primary rounded-l-xl" />

            {/* Vista 2D; cambia al render 3D al hacer hover. */}
            <div className="relative w-28 h-full shrink-0 svg-checker flex items-center justify-center p-2 overflow-hidden">
                <div className={`w-full h-full [&>svg]:w-full [&>svg]:h-full [&>svg]:object-contain transition-opacity ${modelo.path_thumbnail_3d ? 'group-hover:opacity-0' : ''}`}>
                    {modelo.svg_2d && parse(
                        '<?xml version="1.0" encoding="UTF-8" standalone="no"?>' +
                        removeWidthAndHeight(modelo.svg_2d)
                    )}
                </div>
                {modelo.path_thumbnail_3d && (
                    <img
                        src={modelo.path_thumbnail_3d}
                        alt={`Render 3D de ${modelo.name}`}
                        className="absolute inset-0 h-full w-full object-contain bg-base-200 p-2
                                   opacity-0 group-hover:opacity-100 transition-opacity duration-200"
                    />
                )}
            </div>

            {/* Divider */}
            <div className="w-px bg-base-200 shrink-0 my-3" />

            {/* Info */}
            <div className="flex-1 flex flex-col justify-center px-4 min-w-0 gap-1">
                <div className="flex items-center gap-2 min-w-0">
                    <h3 className="font-semibold text-base-content text-base leading-snug truncate"
                        title={capitalize(modelo?.name)}>
                        {capitalize(modelo?.name)}
                    </h3>
                    <ProductTypeBadge modelo={modelo} compact className="shrink-0" />
                </div>
                {/* La fecha sigue al criterio de ordenación activo, para que la
                    lista se lea sola: si ordenas por edición, ves ediciones. */}
                {dateCaption(modelo, sortBy) && (
                    <p className="flex items-center gap-1.5 text-xs text-base-content/40"
                       title={formatAbsolute(sortBy === 'created_at' ? modelo.created_at : modelo.modified_at)}>
                        <Clock className="w-3 h-3 shrink-0" />
                        {dateCaption(modelo, sortBy)}
                    </p>
                )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-1 px-3 shrink-0">
                <button
                    className="btn btn-ghost btn-sm btn-square text-base-content/60 hover:text-primary hover:bg-primary/10"
                    onClick={() => navigate(editorPathForModel(modelo))}
                    title="Editar"
                >
                    <Edit2 className="w-4 h-4" />
                </button>
                <button
                    className="btn btn-ghost btn-sm btn-square text-base-content/60 hover:text-secondary hover:bg-secondary/10"
                    onClick={() => navigate(`/viewer/${modelo.id}/`)}
                    title="Ver en 3D"
                >
                    <Eye className="w-4 h-4" />
                </button>
                {modelo.has_3d !== false && (
                    <ARViewer
                        modelId={modelo.id}
                        revision={modelo.glb_revision || modelo.viewer_revision || modelo.revision || 1}
                        compact
                        inline
                        direct
                    />
                )}
                <ActionsDropdown
                    modelo={modelo}
                    navigate={navigate}
                    authContext={authContext}
                    onDelete={onDelete}
                    onDuplicated={onDuplicated}
                    keyringId={modelo.id}
                    name={modelo.name}
                />
            </div>
        </div>
    );
}

export default Modelo3dCardList;
